/**
 * Source management for recommendation seed queries
 *
 * Provides seed queries for recommendation discovery with priority:
 * 1. Personalized seed queries from localStorage
 * 2. Fallback seed queries (predefined)
 */

import { RecommendationSeed, FALLBACK_SEED_QUERIES, STORAGE_KEYS } from "./types";

/**
 * Get seed queries for recommendation discovery
 *
 * Priority:
 * 1. Personalized seed queries from localStorage (if available and valid)
 * 2. Fallback seed queries (predefined)
 *
 * @param maxQueries Maximum number of seed queries to return
 * @returns Array of seed queries sorted by priority
 */
export function getSeedQueries(maxQueries: number = 5): RecommendationSeed[] {
  const personalizedSeeds = getPersonalizedSeedQueries();
  const fallbackSeeds = FALLBACK_SEED_QUERIES;

  // Combine and sort by priority (personalized first, then fallback)
  const allSeeds = [...personalizedSeeds, ...fallbackSeeds]
    .sort((a, b) => a.priority - b.priority)
    .slice(0, maxQueries);

  return allSeeds;
}

/**
 * Get personalized seed queries from localStorage
 *
 * Returns empty array if no personalized seeds exist or on error.
 */
export function getPersonalizedSeedQueries(): RecommendationSeed[] {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.SEED_QUERIES);
    if (!data) {
      return [];
    }

    const seeds = JSON.parse(data);

    // Validate that parsed data is an array
    if (!Array.isArray(seeds)) {
      console.warn("Personalized seed queries data is not an array, returning empty array");
      clearPersonalizedSeedQueries();
      return [];
    }

    // Validate each seed has required fields
    const validSeeds = seeds.filter(
      (seed): seed is RecommendationSeed =>
        seed &&
        typeof seed === "object" &&
        typeof seed.query === "string" &&
        typeof seed.priority === "number" &&
        (seed.source === "personalized" || seed.source === "fallback")
    );

    if (validSeeds.length !== seeds.length) {
      console.warn("Some personalized seed queries are invalid, filtering them out");
    }

    return validSeeds;
  } catch (error) {
    console.warn("Failed to read personalized seed queries from localStorage:", error);
    return [];
  }
}

/**
 * Save personalized seed queries to localStorage
 *
 * @param seeds Array of personalized seed queries to save
 * @returns true on success, false on error
 */
export function savePersonalizedSeedQueries(seeds: RecommendationSeed[]): boolean {
  try {
    // Ensure all seeds have the correct source
    const personalizedSeeds = seeds.map((seed) => ({
      ...seed,
      source: "personalized" as const,
    }));

    localStorage.setItem(STORAGE_KEYS.SEED_QUERIES, JSON.stringify(personalizedSeeds));
    return true;
  } catch (error) {
    console.warn("Failed to save personalized seed queries to localStorage:", error);
    return false;
  }
}

/**
 * Add a new personalized seed query
 *
 * @param query The search query to add as a seed
 * @param priority Priority level (lower number = higher priority)
 * @returns true on success, false on error
 */
export function addPersonalizedSeedQuery(query: string, priority: number = 1): boolean {
  const currentSeeds = getPersonalizedSeedQueries();

  // Avoid duplicates
  if (currentSeeds.some((seed) => seed.query === query)) {
    return true; // Already exists, consider it a success
  }

  const newSeed: RecommendationSeed = {
    query,
    priority,
    source: "personalized",
  };

  return savePersonalizedSeedQueries([...currentSeeds, newSeed]);
}

/**
 * Clear all personalized seed queries from localStorage
 *
 * @returns true on success, false on error
 */
export function clearPersonalizedSeedQueries(): boolean {
  try {
    localStorage.removeItem(STORAGE_KEYS.SEED_QUERIES);
    return true;
  } catch (error) {
    console.warn("Failed to clear personalized seed queries from localStorage:", error);
    return false;
  }
}

/**
 * Check if personalized seed queries exist in localStorage
 */
export function hasPersonalizedSeedQueries(): boolean {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.SEED_QUERIES);
    return data !== null;
  } catch {
    return false;
  }
}
